import React, { useState, useEffect } from 'react';
import CentrosList from './centros/CentrosList';
import { FileUploaderPreview } from '../../components/FileUploaderPreview';
import { EvidenciaImg } from '../../types/ot.types';

interface ProveedorEditData {
    clave_persona: string;
    clave_personalidad_fiscal: string;
    clave_periodo_pago: string;
    enum_forma_pago: string;
    nombre: string;
    apellido_paterno: string;
    apellido_materno: string;
    razon_social: string;
    direccion: string;
    colonia: string;
    codigo_postal: string;
    clave_estado: string;
    clave_ciudad: string;
    rfc: string;
    clave_estatus: string;
    clave_banco?: string;
    clabe?: string;
    constancia_fiscal_url?: string;
    caratula_estado_cuenta_url?: string;
    cp_clave_proveedor?: number;
}

interface Catalogo { id: string | number; nombre: string; }
interface Estado { clave_estado: number | string; clave_pais: number | string; estado: string; }
interface Ciudad { clave_ciudad: number | string; ciudad: string; clave_estado: number | string; }

const ESTATUS_OPTIONS = [
    { id: 'ACTI', label: 'Activo', color: 'success' },
    { id: 'PEND', label: 'Nuevo a prueba', color: 'primary' },
    { id: 'REVI', label: 'A prueba por servicio', color: 'warning' },
    { id: 'BLOQ', label: 'Bloqueado', color: 'danger' },
    { id: 'CANC', label: 'Eliminado / Dado de baja', color: 'secondary' },
];

const ProveedoresEdit: React.FC = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const proveedorId = urlParams.get('id') || '';

    // --- Estados de Datos ---
    const [proveedor, setProveedor] = useState<ProveedorEditData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [alert, setAlert] = useState<{ type: string; message: string } | null>(null);
    const [fisica, setFisica] = useState<boolean>(false);
    // --- Archivos ---
    const [constanciaFiles, setConstanciaFiles] = useState<EvidenciaImg[]>([]);
    const [caratulaFiles, setCaratulaFiles] = useState<EvidenciaImg[]>([]);

    // --- Catálogos ---
    const [personalidades, setPersonalidades] = useState<Catalogo[]>([]);
    const [periodosPago, setPeriodosPago] = useState<Catalogo[]>([]);
    const [formasPago, setFormasPago] = useState<Catalogo[]>([]);
    const [estados, setEstados] = useState<Estado[]>([]);
    const [ciudades, setCiudades] = useState<Ciudad[]>([]);
    const [bancos, setBancos] = useState<Catalogo[]>([]);

    // --- Estados Lateral ---
    const [nuevoRfc, setNuevoRfc] = useState<string>('');
    const [isChangingRfc, setIsChangingRfc] = useState<boolean>(false);
    const [nuevoEstatus, setNuevoEstatus] = useState<string>('');
    const [comentarioEstatus, setComentarioEstatus] = useState<string>('');
    const [isChangingEstatus, setIsChangingEstatus] = useState<boolean>(false);

    const fetchSeguro = async (url: string, options?: RequestInit) => {
        const response = await fetch(url, options);
        const data = await response.json();
        if (data && data.status === 'error' && data.message && data.message.includes('Sesión inválida')) {
            window.location.href = '/';
            throw new Error("Sesión expirada. Redirigiendo...");
        }
        return data;
    };

    useEffect(() => {
        if (proveedorId) fetchInitialData();
        else setAlert({ type: 'danger', message: 'ID de proveedor no especificado.' });
    }, [proveedorId]);

    useEffect(() => {
        if (proveedor?.clave_estado) fetchCiudades(proveedor.clave_estado);
        else setCiudades([]);
    }, [proveedor?.clave_estado]);

    const fetchInitialData = async () => {
        setIsLoading(true);
        try {
            const [provRes, catPersonalidad, catPeriodos, catFormas, catEstados, catBancos] = await Promise.all([
                fetchSeguro(`/sittApi/proveedores/obtener.php?id=${proveedorId}`).then(res => res),
                fetchSeguro('/sittApi/catalogos/personalidad_fiscal.php').then(res => res),
                fetchSeguro('/sittApi/catalogos/periodos_pago.php').then(res => res),
                fetchSeguro('/sittApi/catalogos/formas_pago.php').then(res => res),
                fetchSeguro('/sittApi/obtener_estados.php').then(res => res),
                fetchSeguro('/sittApi/catalogos/bancos.php').then(res => res).catch(() => ({ data: [] })),
                // fetchSeguro('/sittApi/proveedores/obtener_archivos.php').then(res => res).catch(() => ({ data: [] }))
                 ]);
            // setConstanciaFiles(catArchivos.filter((a: any) => a.contexto === "TAX_CERTIFICATE"));
            // setCaratulaFiles(catArchivos.filter((a: any) => a.contexto === "BANK_COVER"));
            if (provRes.status === 'success') {
                setProveedor(provRes.data);
                setNuevoRfc(provRes.data.rfc);
                setNuevoEstatus(provRes.data.clave_estatus);
                setFisica(provRes.data.clave_personalidad_fiscal);
                try {
                    const archivosRes = await fetchSeguro('/sittApi/proveedores/obtener_archivos.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ clave_persona_proveedor: proveedorId })
                    });

                    if (archivosRes.status === 'success' && archivosRes.data?.archivos) {
                        const allFiles = archivosRes.data.archivos;
                        setConstanciaFiles(allFiles.filter((a: any) => a.contexto === 'TAX_CERTIFICATE' && a.clave_centro == null));
                        setCaratulaFiles(allFiles.filter((a: any) => a.contexto === 'BANK_COVER' && a.clave_centro == null));
                    }
                } catch (e) {
                    console.warn("No se pudieron cargar los archivos del proveedor", e);
                }
            }

            setPersonalidades(catPersonalidad.data || []);
            setPeriodosPago(catPeriodos.data || []);
            setFormasPago(catFormas.data || []);
            setEstados(catEstados.data || []);
            setBancos(catBancos.data || catBancos || []);
            
        } catch (error) {
            setAlert({ type: 'danger', message: 'Error de red al cargar la información del proveedor.' });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCiudades = async (idEstado: string) => {
        try {
            const result = await fetchSeguro("/api/catalogos/ciudades.php", {
                method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({ accion: "filtered", idEstado: idEstado }),
            });
            setCiudades(result || []);
        } catch (error) { console.error("Error al cargar ciudades", error); }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setProveedor(prev => prev ? { ...prev, [name]: value } : null);

        if (name === 'clave_personalidad_fiscal' && value !== '1') {
            setProveedor(prev => prev ? { ...prev, nombre: '', apellido_paterno: '', apellido_materno: '' } : null);
        } else if (name === 'clave_personalidad_fiscal' && value === '1') {
            setProveedor(prev => prev ? { ...prev, razon_social: '' } : null);
        }
    };

    const handleSaveGeneral = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        setAlert(null);
        try {
            const payload = new FormData();

            if (proveedor) {
                Object.keys(proveedor).forEach(key => {
                    const val = (proveedor as any)[key];
                    payload.append(key, val === null || val === undefined ? '' : String(val));
                });
            }

            if (constanciaFiles.length > 0 && constanciaFiles[0].file) {
                payload.append('tax_certificate', constanciaFiles[0].file, constanciaFiles[0].nombreOriginal);
            }
            if (caratulaFiles.length > 0 && caratulaFiles[0].file) {
                payload.append('bank_cover', caratulaFiles[0].file, caratulaFiles[0].nombreOriginal);
            }

            const result = await fetchSeguro('/sittApi/proveedores/actualizar.php', {
                method: 'POST', body: payload
            });

            if (result.status === 'success') {
                setAlert({ type: 'success', message: 'Proveedor actualizado correctamente. Los centros espejo han sido sincronizados.' });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else throw new Error(result.message);
        } catch (error: any) {
            setAlert({ type: 'danger', message: error.message || 'Error al guardar.' });
        } finally { setIsSaving(false); }
    };

    const handleChangeRfc = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!window.confirm("¿Desea actualizar el RFC? ATENCIÓN: Esto cancelará lógicamente todos los centros asociados actuales ya que se considera una nueva entidad fiscal.")) return;
        setIsChangingRfc(true);
        try {
            const result = await fetchSeguro(`/sittApi/proveedores/cambiar_rfc.php`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: proveedorId, rfc: nuevoRfc })
            });
            if (result.status === 'success') {
                setAlert({ type: 'success', message: 'RFC actualizado. Los centros anteriores han sido dados de baja por seguridad fiscal.' });
                setProveedor(prev => prev ? { ...prev, rfc: nuevoRfc } : null);
                // Opcional: Recargar la página para que la tabla de centros se limpie visualmente
            } else throw new Error(result.message);
        } catch (error: any) {
            setAlert({ type: 'danger', message: error.message || 'Error al cambiar RFC.' });
        } finally { setIsChangingRfc(false); }
    };

    const handleChangeEstatus = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!comentarioEstatus.trim()) {
            setAlert({ type: 'warning', message: "Debe escribir un motivo o comentario para la bitácora." });
            return;
        }

        if (!window.confirm(`¿Seguro que desea cambiar el estatus a ${ESTATUS_OPTIONS.find(o => o.id === nuevoEstatus)?.label}?`)) return;

        setIsChangingEstatus(true);
        try {
            const result = await fetchSeguro(`/sittApi/proveedores/cambiar_estatus.php`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: proveedorId, estatus: nuevoEstatus, comentario: comentarioEstatus })
            });
            if (result.status === 'success') {
                setAlert({ type: 'success', message: 'Estatus del proveedor actualizado correctamente.' });
                setProveedor(prev => prev ? { ...prev, clave_estatus: nuevoEstatus } : null);
                setComentarioEstatus('');
            } else throw new Error(result.message);
        } catch (error: any) {
            setAlert({ type: 'danger', message: error.message || 'Error al cambiar el estatus.' });
        } finally { setIsChangingEstatus(false); }
    };

    const handleDeleteRapido = async (e: React.FormEvent) => {
        e.preventDefault();
        setNuevoEstatus('CANC');
        setAlert({ type: 'info', message: "Por favor, escriba el motivo de la baja en la tarjeta de Estado Operativo y presione 'Aplicar Cambio'." });
        document.getElementById('comentarioEstatus')?.focus();
    };

    if (isLoading || !proveedor) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
                <span className="ms-3 text-muted">Cargando información del proveedor...</span>
            </div>
        );
    }


    return (
        <div className="container-fluid px-0 animate__animated animate__fadeIn">
            {alert && (
                <div className={`alert alert-${alert.type} shadow-sm border-0 alert-dismissible fade show`}>
                    <i className={`fas ${alert.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                    {alert.message}
                    <button type="button" className="btn-close" onClick={() => setAlert(null)}></button>
                </div>
            )}

            <div className="row g-4">
                <div className="col-xl-9">
                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-header border-bottom py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 fw-bold">
                                <i className="fas fa-edit text-primary me-2"></i> Editar Proveedor (Matriz)
                            </h5>
                            <span className={`badge bg-${ESTATUS_OPTIONS.find(o => o.id === proveedor.clave_estatus)?.color || 'secondary'}`}>
                                {ESTATUS_OPTIONS.find(o => o.id === proveedor.clave_estatus)?.label || proveedor.clave_estatus}
                            </span>
                        </div>
                        <div className="card-body p-4">
                            <form onSubmit={handleSaveGeneral}>
                                {/* --- SECCIÓN: GENERAL --- */}
                                <h6 className="fw-bold text-secondary text-uppercase border-bottom pb-2 mb-3">General</h6>
                                <div className="row g-3 mb-4">
                                    <div className="col-lg-3">
                                        <label className="fw-bold mb-1 small text-muted">Personalidad fiscal</label>
                                        <select className="form-select shadow-sm" name="clave_personalidad_fiscal" value={proveedor.clave_personalidad_fiscal} onChange={handleChange} required>
                                            <option value="">Elegir...</option>
                                            {personalidades.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-lg-3">
                                        <label className="fw-bold mb-1 small text-muted">Periodo de pago</label>
                                        <select className="form-select shadow-sm" name="clave_periodo_pago" value={proveedor.clave_periodo_pago} onChange={handleChange} required>
                                            <option value="">Elegir...</option>
                                            {periodosPago.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-lg-3">
                                        <label className="fw-bold mb-1 small text-muted">Forma de pago</label>
                                        <select className="form-select shadow-sm" name="enum_forma_pago" value={proveedor.enum_forma_pago} onChange={handleChange}>
                                            <option value="">Elegir...</option>
                                            {formasPago.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div className="row g-3 mb-5">
                                    {fisica == true ? (
                                        <>
                                            <div className="col-lg-4">
                                                <label className="fw-bold mb-1 small text-muted">Nombre</label>
                                                <input type="text" className="form-control shadow-sm" name="nombre" value={proveedor.nombre} onChange={handleChange} required />
                                            </div>
                                            <div className="col-lg-4">
                                                <label className="fw-bold mb-1 small text-muted">Apellido paterno</label>
                                                <input type="text" className="form-control shadow-sm" name="apellido_paterno" value={proveedor.apellido_paterno} onChange={handleChange} required />
                                            </div>
                                            <div className="col-lg-4">
                                                <label className="fw-bold mb-1 small text-muted">Apellido materno</label>
                                                <input type="text" className="form-control shadow-sm" name="apellido_materno" value={proveedor.apellido_materno} onChange={handleChange} />
                                            </div>
                                        </>
                                    ) : (
                                        <div className="col-lg-8">
                                            <label className="fw-bold mb-1 small text-muted">Razón social</label>
                                            <input type="text" className="form-control shadow-sm" name="razon_social" value={proveedor.razon_social} onChange={handleChange} required />
                                        </div>
                                    )}
                                </div>

                                {/* --- SECCIÓN: BANCARIA Y FISCAL (NUEVA) --- */}
                                <h6 className="fw-bold text-secondary text-uppercase border-bottom pb-2 mb-3 mt-4">Fiscal y Bancario (Matriz)</h6>
                                <div className="alert alert-light border small text-muted mb-4">
                                    <i className="fas fa-info-circle me-1 text-primary"></i> Esta información será heredada automáticamente a los Centros/Sucursales asociados.
                                </div>
                                <div className="row g-3 mb-4">
                                    <div className="col-lg-6">
                                        <label className="fw-bold mb-1 small text-muted">Banco Predeterminado</label>
                                        <select className="form-select shadow-sm" name="clave_banco" value={proveedor.clave_banco || ''} onChange={handleChange}>
                                            <option value="">Elegir...</option>
                                            {bancos.map(b => <option key={b.id || (b as any).clave_banco} value={b.id || (b as any).clave_banco}>{b.nombre || (b as any).banco}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-lg-6">
                                        <label className="fw-bold mb-1 small text-muted">CLABE Interbancaria</label>
                                        <input type="text" className="form-control shadow-sm" name="clabe" value={proveedor.clabe || ''} onChange={handleChange} maxLength={18} pattern="[0-9]{18}" placeholder="18 dígitos numéricos" />
                                    </div>
                                </div>
                                <div className="row g-3 mb-5">
                                    <div className="col-lg-6">
                                        <FileUploaderPreview label="Constancia de Situación Fiscal" archivos={constanciaFiles} setArchivos={setConstanciaFiles} accept="application/pdf" multiple={false} />
                                    </div>
                                    <div className="col-lg-6">
                                        <FileUploaderPreview label="Carátula de Estado de Cuenta" archivos={caratulaFiles} setArchivos={setCaratulaFiles} accept="application/pdf" multiple={false} />
                                    </div>
                                </div>

                                {/* --- SECCIÓN: DIRECCIÓN --- */}
                                <h6 className="fw-bold text-secondary text-uppercase border-bottom pb-2 mb-3 mt-4">Dirección Central</h6>
                                <div className="row g-3 mb-3">
                                    <div className="col-lg-6">
                                        <label className="fw-bold mb-1 small text-muted">Calle y número</label>
                                        <input type="text" className="form-control shadow-sm" name="direccion" value={proveedor.direccion} onChange={handleChange} />
                                    </div>
                                    <div className="col-lg-4">
                                        <label className="fw-bold mb-1 small text-muted">Colonia</label>
                                        <input type="text" className="form-control shadow-sm" name="colonia" value={proveedor.colonia} onChange={handleChange} />
                                    </div>
                                    <div className="col-lg-2">
                                        <label className="fw-bold mb-1 small text-muted">C.P.</label>
                                        <input type="number" className="form-control shadow-sm" name="codigo_postal" value={proveedor.codigo_postal} onChange={handleChange} />
                                    </div>
                                </div>

                                <div className="row g-3 mb-4">
                                    <div className="col-lg-4">
                                        <label className="fw-bold mb-1 small text-muted">Estado</label>
                                        <select className="form-select shadow-sm" name="clave_estado" value={proveedor.clave_estado} onChange={handleChange}>
                                            <option value="">Elegir...</option>
                                            {estados.map(e => <option key={e.clave_estado} value={e.clave_estado}>{e.estado}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-lg-4">
                                        <label className="fw-bold mb-1 small text-muted">Ciudad</label>
                                        <select className="form-select shadow-sm" name="clave_ciudad" value={proveedor.clave_ciudad} onChange={handleChange} disabled={!proveedor.clave_estado}>
                                            <option value="">{proveedor.clave_estado ? 'Elegir...' : 'Seleccione un estado primero'}</option>
                                            {ciudades.map(c => <option key={c.clave_ciudad} value={c.clave_ciudad}>{c.ciudad}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div className="text-end border-top pt-3">
                                    <button type="submit" className="btn btn-success px-5 fw-bold shadow-sm" disabled={isSaving}>
                                        {isSaving ? <><i className="fas fa-spinner fa-spin me-2"></i> Procesando...</> : <><i className="fas fa-save me-2"></i> Guardar Corporativo</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className="mt-5">
                        <CentrosList proveedorId={proveedorId} />
                    </div>
                </div>

                <div className="col-xl-3">
                    <div className="card shadow-sm border-0 mb-4 border-top border-3 border-primary">
                        <div className="card-body">
                            <form onSubmit={handleChangeEstatus}>
                                <h6 className="fw-bold mb-3"><i className="fas fa-shield-alt text-primary me-2"></i> Estado Operativo</h6>

                                <div className="form-group mb-3">
                                    <label className="fw-bold mb-1 small text-muted">Estatus del Proveedor</label>
                                    <select
                                        className="form-select form-select-sm shadow-sm"
                                        value={nuevoEstatus}
                                        onChange={(e) => setNuevoEstatus(e.target.value)}
                                        required
                                    >
                                        {ESTATUS_OPTIONS.map(opt => (
                                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group mb-3">
                                    <label className="fw-bold mb-1 small text-muted">Motivo / Comentario en Bitácora *</label>
                                    <textarea
                                        className="form-control form-control-sm shadow-sm"
                                        rows={3}
                                        placeholder="Indique el motivo del cambio para la auditoría..."
                                        value={comentarioEstatus}
                                        onChange={(e) => setComentarioEstatus(e.target.value)}
                                        id="comentarioEstatus"
                                        required
                                    ></textarea>
                                </div>

                                <div className="d-grid">
                                    <button
                                        type="submit"
                                        className="btn btn-sm btn-primary fw-bold"
                                        disabled={isChangingEstatus || (nuevoEstatus === proveedor.clave_estatus && !comentarioEstatus.trim())}
                                    >
                                        {isChangingEstatus ? 'Aplicando...' : 'Aplicar Cambio'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className="card shadow-sm border-0 mb-4 border-top border-3 border-danger">
                        <div className="card-body">
                            <form onSubmit={handleChangeRfc}>
                                <h6 className="fw-bold mb-3"><i className="fas fa-id-card text-danger me-2"></i> Reestructuración (RFC)</h6>
                                <p className="small text-muted mb-2 lh-sm" style={{ fontSize: '11px' }}>Cambiar el RFC provocará la <strong>baja lógica</strong> de todos los centros actuales para proteger el historial fiscal.</p>
                                <div className="form-group mb-3">
                                    <input
                                        className="form-control form-control-sm text-uppercase font-monospace shadow-sm"
                                        type="text"
                                        maxLength={13}
                                        value={nuevoRfc}
                                        onChange={(e) => setNuevoRfc(e.target.value.toUpperCase())}
                                        required
                                    />
                                </div>
                                <div className="d-grid">
                                    <button type="submit" className="btn btn-sm btn-outline-danger fw-bold" disabled={isChangingRfc || nuevoRfc === proveedor.rfc}>
                                        {isChangingRfc ? 'Actualizando...' : 'Cambiar RFC e invalidar centros'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProveedoresEdit;